import moment from 'moment';
import type { DeliveryAddressRequest } from './api';
import type { SubscriptionData } from '../subscription/types';
import { getSubscriptionPlanTitleFromMerged } from '../subscription/planTitle';
import { formatMomentForApiYmd, normalizeCalendarDate } from '../../../shared/utils/dateUtils';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';

export type DeliveryDayCellState = 'assigned' | 'available' | 'off_plan' | 'disabled';

/** ASCII `YYYY-MM-DD` — never use raw `moment.format` under Arabic locale. */
function asAsciiYmd(value: string | undefined | null): string {
  if (value == null || value === '') {
    return '';
  }
  return ensureAsciiDateString(value) || normalizeCalendarDate(value);
}

function asAsciiYmdSet(dates: string[] | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const d of dates ?? []) {
    const n = asAsciiYmd(d);
    if (n) {
      out.add(n);
    }
  }
  return out;
}

export function generateDeliveryAddressRequests(
  subscriptions: SubscriptionData[],
  selectedDates: string[],
  customerId: number,
  addressId: number,
): DeliveryAddressRequest[] {
  const requests: DeliveryAddressRequest[] = [];

  for (const sub of subscriptions) {
    const sub_id = sub.id ?? (sub as { customer_subscription_id?: number }).customer_subscription_id;
    if (!sub_id || !sub.start_date || !sub.end_date) {
      continue;
    }

    const start_str = formatMomentForApiYmd(moment(sub.start_date));
    const end_str = formatMomentForApiYmd(moment(sub.end_date));

    const valid_dates = selectedDates
      .map(d => asAsciiYmd(d))
      .filter(d => {
        const m = moment(d, 'YYYY-MM-DD', true);
        return m.isValid() && m.isSameOrAfter(start_str, 'day') && m.isSameOrBefore(end_str, 'day');
      });

    if (valid_dates.length > 0) {
      requests.push({
        customer_id: customerId,
        customer_subscription_id: sub_id,
        dates: valid_dates,
        address_id: addressId,
      });
    }
  }

  return requests;
}

export function getSubscriptionDateRange(sub: SubscriptionData): { start: string; end: string } | null {
  if (!sub.start_date || !sub.end_date) {
    return null;
  }
  const start = moment(sub.start_date);
  const end = moment(sub.end_date);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return { start: formatMomentForApiYmd(start), end: formatMomentForApiYmd(end) };
}

export function isDateInSubscriptionWindow(date_str: string, sub: SubscriptionData): boolean {
  const range = getSubscriptionDateRange(sub);
  if (!range) {
    return false;
  }
  const ascii = asAsciiYmd(date_str);
  const m = moment(ascii, 'YYYY-MM-DD', true);
  return m.isValid() && m.isSameOrAfter(range.start, 'day') && m.isSameOrBefore(range.end, 'day');
}

export function resolveDeliveryDayCellState(
  date_str: string,
  available_dates: string[],
  selected_dates: string[],
  subscription: SubscriptionData | null,
): DeliveryDayCellState {
  const ascii = asAsciiYmd(date_str);
  const m = moment(ascii, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return 'disabled';
  }
  const today = moment().startOf('day');
  if (m.isBefore(today, 'day')) {
    return 'disabled';
  }
  const available_set = asAsciiYmdSet(available_dates);
  if (!available_set.has(ascii)) {
    return 'disabled';
  }
  if (subscription && !isDateInSubscriptionWindow(ascii, subscription)) {
    return 'off_plan';
  }
  if (asAsciiYmdSet(selected_dates).has(ascii)) {
    return 'assigned';
  }
  return 'available';
}

export type WeekStripDay = {
  date: string;
  day_of_month: number;
  weekday_label: string;
  state: DeliveryDayCellState;
};

export function buildWeekStripDays(
  week_start: moment.Moment,
  available_dates: string[],
  selected_dates: string[],
  subscription: SubscriptionData | null,
  locale: string,
): WeekStripDay[] {
  const loc = locale === 'ar' ? 'ar' : 'en';
  const days: WeekStripDay[] = [];
  for (let i = 0; i < 7; i++) {
    const day = week_start.clone().add(i, 'day');
    const date_str = formatMomentForApiYmd(day);
    days.push({
      date: date_str,
      day_of_month: day.date(),
      weekday_label: day.clone().locale(loc).format('ddd'),
      state: resolveDeliveryDayCellState(date_str, available_dates, selected_dates, subscription),
    });
  }
  return days;
}

export type ScheduleCalendarDay = {
  date: string;
  day_of_month: number;
  is_padding: boolean;
  is_disabled: boolean;
  address_id: number | null;
  is_target_assigned: boolean;
};

export function buildScheduleMonthDays(
  month: moment.Moment,
  available_dates: string[],
  selected_dates: string[],
  subscriptions: SubscriptionData[],
  target_address_id: number,
): ScheduleCalendarDay[] {
  const days: ScheduleCalendarDay[] = [];
  const start_of_month = month.clone().startOf('month');
  const end_of_month = month.clone().endOf('month');
  const today = moment().startOf('day');
  const first_dow = start_of_month.day();
  const available_set = asAsciiYmdSet(available_dates);
  const selected_set = asAsciiYmdSet(selected_dates);

  for (let i = 0; i < first_dow; i++) {
    days.push({
      date: '',
      day_of_month: 0,
      is_padding: true,
      is_disabled: true,
      address_id: null,
      is_target_assigned: false,
    });
  }

  let cursor = start_of_month.clone();
  while (cursor.isSameOrBefore(end_of_month, 'day')) {
    const date_str = formatMomentForApiYmd(cursor);
    const is_past = cursor.isBefore(today, 'day');
    const is_available = available_set.has(date_str);
    const address_id = getDeliveryAddressIdForDate(date_str, subscriptions);
    days.push({
      date: date_str,
      day_of_month: cursor.date(),
      is_padding: false,
      is_disabled: is_past || !is_available,
      address_id,
      is_target_assigned: selected_set.has(date_str),
    });
    cursor.add(1, 'day');
  }

  return days;
}

export function getDeliveryAddressIdForDate(date_str: string, subscriptions: SubscriptionData[]): number | null {
  const target = asAsciiYmd(date_str);
  for (const sub of subscriptions) {
    const particulars = sub.particularsDaily ?? [];
    for (const row of particulars) {
      const row_date = row.date ? asAsciiYmd(String(row.date)) : '';
      if (row_date && row_date === target) {
        const addr = Number((row as { address_id?: number | string }).address_id);
        if (Number.isFinite(addr) && addr > 0) {
          return addr;
        }
      }
    }
  }
  return null;
}

/** Localized weekday abbreviations for assigned dates, e.g. Tue, Thu, Sat */
export function getDeliveryWeekdayAbbreviations(dates: string[], locale: string): string[] {
  const loc = locale === 'ar' ? 'ar' : 'en';
  const sorted = [...dates]
    .map(d => asAsciiYmd(d))
    .filter(Boolean)
    .sort((a, b) => moment(a).diff(moment(b)));
  const seen = new Set<number>();
  const labels: string[] = [];
  for (const d of sorted) {
    const m = moment(d, 'YYYY-MM-DD', true);
    if (!m.isValid()) {
      continue;
    }
    const dow = m.day();
    if (seen.has(dow)) {
      continue;
    }
    seen.add(dow);
    labels.push(m.clone().locale(loc).format('ddd'));
  }
  return labels;
}

function upcomingYmdDates(dates: string[], today = moment().startOf('day')): string[] {
  return [...asAsciiYmdSet(dates)]
    .filter(d => {
      const m = moment(d, 'YYYY-MM-DD', true);
      return m.isValid() && m.isSameOrAfter(today, 'day');
    })
    .sort((a, b) => moment(a).diff(moment(b)));
}

/** 1 = next occurrence of that weekday from today (incl. today), 2 = second, … */
export function getWeekdayOccurrenceFromNow(date_str: string, today = moment().startOf('day')): number {
  const target = moment(asAsciiYmd(date_str), 'YYYY-MM-DD', true).startOf('day');
  if (!target.isValid() || target.isBefore(today, 'day')) {
    return 0;
  }
  const dow = target.day();
  let count = 0;
  const cursor = today.clone();
  while (cursor.isSameOrBefore(target, 'day')) {
    if (cursor.day() === dow) {
      count += 1;
    }
    cursor.add(1, 'day');
  }
  return count;
}

function englishOrdinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function arabicOrdinal(n: number): string {
  const map: Record<number, string> = {
    1: 'الأول',
    2: 'الثاني',
    3: 'الثالث',
    4: 'الرابع',
    5: 'الخامس',
    6: 'السادس',
    7: 'السابع',
    8: 'الثامن',
    9: 'التاسع',
    10: 'العاشر',
  };
  return map[n] ?? `${n}`;
}

/**
 * Relative weekday label for one delivery date, e.g. "Next Monday" / "2nd Monday".
 */
export function formatRelativeWeekdayDeliveryLabel(date_str: string, locale: string): string {
  const loc = locale === 'ar' ? 'ar' : 'en';
  const ascii = asAsciiYmd(date_str);
  const m = moment(ascii, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return '';
  }
  const occurrence = getWeekdayOccurrenceFromNow(ascii);
  if (occurrence <= 0) {
    return '';
  }
  const weekday = m.clone().locale(loc).format('dddd');
  if (occurrence === 1) {
    return loc === 'ar' ? `${weekday} القادم` : `Next ${weekday}`;
  }
  if (loc === 'ar') {
    return `${weekday} ${arabicOrdinal(occurrence)}`;
  }
  return `${englishOrdinal(occurrence)} ${weekday}`;
}

/**
 * Address card delivery line:
 * - Upcoming particulars only (past ignored).
 * - If this address owns **every** remaining delivery on a weekday → one compact abbrev (`Mon`).
 * - Otherwise list each date as Next / 2nd / 3rd weekday so every assigned case is shown.
 */
export function buildAddressDeliverySummaryLabels(
  assigned_dates: string[],
  all_delivery_dates: string[],
  locale: string,
): string[] {
  const today = moment().startOf('day');
  const assigned = upcomingYmdDates(assigned_dates, today);
  if (assigned.length === 0) {
    return [];
  }
  const all_upcoming = upcomingYmdDates(all_delivery_dates, today);

  const all_count_by_dow = new Map<number, number>();
  for (const d of all_upcoming) {
    const dow = moment(d, 'YYYY-MM-DD', true).day();
    all_count_by_dow.set(dow, (all_count_by_dow.get(dow) ?? 0) + 1);
  }
  const assigned_count_by_dow = new Map<number, number>();
  for (const d of assigned) {
    const dow = moment(d, 'YYYY-MM-DD', true).day();
    assigned_count_by_dow.set(dow, (assigned_count_by_dow.get(dow) ?? 0) + 1);
  }

  const fully_owned = new Set<number>();
  for (const [dow, count] of assigned_count_by_dow) {
    if (count > 0 && count === (all_count_by_dow.get(dow) ?? 0)) {
      fully_owned.add(dow);
    }
  }

  const loc = locale === 'ar' ? 'ar' : 'en';
  const labels: string[] = [];
  const seen_compact = new Set<number>();

  for (const d of assigned) {
    const m = moment(d, 'YYYY-MM-DD', true);
    const dow = m.day();
    if (fully_owned.has(dow)) {
      if (seen_compact.has(dow)) {
        continue;
      }
      seen_compact.add(dow);
      labels.push(m.clone().locale(loc).format('ddd'));
      continue;
    }
    const relative = formatRelativeWeekdayDeliveryLabel(d, locale);
    if (relative) {
      labels.push(relative);
    }
  }

  return labels;
}

/** Every particular date that has a delivery address (any address). */
export function getAllParticularDeliveryDates(subscriptions: SubscriptionData[]): string[] {
  const dates: string[] = [];
  for (const sub of Array.isArray(subscriptions) ? subscriptions : []) {
    for (const p of sub.particularsDaily ?? []) {
      const addr = Number((p as { address_id?: number | string }).address_id);
      if (!Number.isFinite(addr) || addr <= 0) {
        continue;
      }
      if (p.date) {
        const ascii = asAsciiYmd(String(p.date));
        if (ascii && moment(ascii, 'YYYY-MM-DD', true).isValid()) {
          dates.push(ascii);
        }
      }
    }
  }
  return [...new Set(dates)];
}

export function getSubscriptionLabel(sub: SubscriptionData, fallback: string): string {
  return getSubscriptionPlanTitleFromMerged(sub, fallback);
}
